import {
  BarChart3,
  Building2,
  Import,
  KanbanSquare,
  Megaphone,
  Palette,
  Radar,
  SlidersHorizontal,
  Swords,
  UserRound,
  Users,
} from "lucide-react";

import {
  HUB_ENTRIES,
  type NavEntry,
} from "@/components/fretum/common/navEntries";

import { MARKET_BASE } from "@/components/market-intel/routes";
import { COMPETITORS_BASE } from "@/components/competitors/routes";
import { MARKETING_BASE } from "@/components/marketing/routes";

import { ImportPage } from "../misc/ImportPage";

/** Mobile path of the desktop /settings (CRM configuration) page. On a phone
 * /settings is the personal account page, so the admin one lives here. */
export const MOBILE_CRM_SETTINGS_PATH = "/settings/crm";

export type MobileModuleEntry = NavEntry & {
  /** Same access check as the desktop entry, if it has one. */
  access?: { resource: string; action: string };
};

/**
 * Desktop modules with no slot in the tab bar or the '+' panel: the sidebar's
 * team tasks and styles, then its "Hub" section in the same order.
 */
export const MOBILE_MODULES: MobileModuleEntry[] = [
  { to: "/team_tasks", icon: KanbanSquare, label: "crm.mobile.team_tasks" },
  { to: "/styles", icon: Palette, label: "hub.nav.styles" },
  ...HUB_ENTRIES,
];

/** Mobile "Más" screen: a list with every module that has no tab of its own. */
export const MOBILE_MORE_PATH = "/more";

/**
 * The full calendar page (week / month / agenda views). Not listed in any
 * menu: on a phone the top bar's calendar button opens it as a window. The
 * route stays for old links (and /agenda redirects here).
 */
export const MOBILE_CALENDAR_PATH = "/calendar";

/**
 * Destinations without a tab in the bottom bar (home, tasks and deals are
 * tabs; the market, a tab too, keeps its row). The calendar is not listed:
 * the top bar's calendar button opens it.
 */
const MOBILE_MORE_CRM: MobileModuleEntry[] = [
  { to: "/contacts", icon: UserRound, label: "resources.contacts.name" },
  { to: "/companies", icon: Building2, label: "resources.companies.name" },
  { to: "/informes", icon: BarChart3, label: "crm.mobile.nav_reports" },
  // A mobile page of its own (not a desktop page reused as-is).
  { to: MARKET_BASE, icon: Radar, label: "crm.mobile.nav_market" },
  { to: COMPETITORS_BASE, icon: Swords, label: "competition.title" },
  { to: MARKETING_BASE, icon: Megaphone, label: "marketing.title" },
];

export interface MobileMoreGroup {
  id: "crm" | "hub";
  /** Translation key of the group header. */
  title: string;
  entries: MobileModuleEntry[];
}

/** The "Más" screen's grouped lists: the CRM pages, then the hub modules. */
export const MOBILE_MORE_GROUPS: readonly MobileMoreGroup[] = [
  { id: "crm", title: "crm.mobile.more_groups.crm", entries: MOBILE_MORE_CRM },
  { id: "hub", title: "crm.mobile.more_groups.hub", entries: MOBILE_MODULES },
];

/** Every row of the "Más" screen, top to bottom. */
export const MOBILE_MORE_ENTRIES: MobileModuleEntry[] =
  MOBILE_MORE_GROUPS.flatMap((group) => group.entries);

/** The desktop user menu's admin entries, gated the same way. */
export const MOBILE_ADMIN_ENTRIES: MobileModuleEntry[] = [
  {
    to: "/sales",
    icon: Users,
    label: "resources.sales.name",
    access: { resource: "sales", action: "list" },
  },
  {
    to: MOBILE_CRM_SETTINGS_PATH,
    icon: SlidersHorizontal,
    label: "crm.mobile.crm_settings",
    access: { resource: "configuration", action: "edit" },
  },
  { to: ImportPage.path, icon: Import, label: "crm.header.import_data" },
];

const DESKTOP_PAGE_PREFIXES = [
  ...MOBILE_MODULES,
  ...MOBILE_ADMIN_ENTRIES,
  { to: "/profile" },
  { to: "/events" },
  { to: "/deals" },
].map((entry) => entry.to);

/**
 * True on the desktop pages reused as-is on a phone. They expect the desktop
 * layout's gutter, which mobile pages add themselves (MobileContent).
 */
export const isDesktopPagePath = (pathname: string): boolean =>
  DESKTOP_PAGE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
